import useVisibleProjects from "../hooks/useVisibleProjects";
// src/pages/DashboardPage.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  MoreHorizontal,
  Users,
  UserPlus,
  Building2,
  FileText,
  Clock,
  CalendarX,
  Wallet,
  LayoutDashboard,
} from "lucide-react";
import { FaRegCalendarAlt } from "react-icons/fa";
import { collection, onSnapshot, doc } from "firebase/firestore";
import { db } from "../firebase";
import { useCompany } from "../contexts/CompanyContext";
import { useAuth } from "../contexts/AuthContext";
import LoadingIndicator from "../components/LoadingIndicator";
import PayrollTrendChart from "../components/PayrollTrendChart";

// Global cache - delas mellan alla sidor för att minska Firestore-anrop
const globalCache = {
  shifts: null,
  shiftsCompanyUid: null,
  shiftsLoaded: false,
  shiftsUnsubscribe: null,
  
  employees: null,
  employeesCompanyUid: null,
  employeesLoaded: false,
  employeesUnsubscribe: null,
  
  projects: null,
  projectsCompanyUid: null,
  projectsLoaded: false,
  projectsUnsubscribe: null,
  
  customers: null,
  customersCompanyUid: null,
  customersLoaded: false,
  customersUnsubscribe: null,
  
  overtimeRequests: null,
  overtimeCompanyUid: null,
  overtimeLoaded: false,
  overtimeUnsubscribe: null,
  
  absenceRequests: null,
  absenceCompanyUid: null,
  absenceLoaded: false,
  absenceUnsubscribe: null,
  
  reports: null,
  reportsCompanyUid: null,
  reportsLoaded: false,
  reportsUnsubscribe: null,
  
  // Cache för företagsägaren
  ownerInfo: null,
  ownerCompanyUid: null,
  
  // Cache för shift IDs
  shiftIds: null,
  shiftIdsCompanyUid: null,
  shiftIdsLoaded: false,
  shiftIdsUnsubscribe: null,
};

const palette = {
  bg: "#f5f6fb",
  card: "#ffffff",
  border: "#eef0f6",
  text: "#1f2433",
  muted: "#8f97ad",
  purple: "#6c2bd9",
  purpleDark: "#5b18d6",
  orange: "#ff8a4c",
  yellow: "#f59e0b",
  yellowLight: "#fef3c7",
  red: "#ef4444",
  redLight: "#fee2e2",
  indigo: "#4f46e5",
  indigoLight: "#e0e7ff",
};

const shellStyle = {
  minHeight: "100vh",
  background: palette.bg,
  display: "block",
  fontFamily:
    'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  color: palette.text,
};

const cardStyle = {
  background: palette.card,
  borderRadius: "18px",
  border: `1px solid ${palette.border}`,
  boxShadow: "0 8px 30px rgba(31, 36, 51, 0.05)",
};

function safeParseDate(dateValue) {
  if (!dateValue) return null;

  try {
    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? null : dateValue;
    }

    if (
      dateValue &&
      typeof dateValue === "object" &&
      typeof dateValue.toDate === "function"
    ) {
      const parsed = dateValue.toDate();
      return parsed instanceof Date && !isNaN(parsed.getTime()) ? parsed : null;
    }

    if (typeof dateValue === "string") {
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? null : date;
    }

    if (typeof dateValue === "number") {
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? null : date;
    }

    return null;
  } catch (error) {
    console.error("Fel vid parsning av datum:", error);
    return null;
  }
}

function timeToMinutes(timeString) {
  if (!timeString || typeof timeString !== "string") return 0;
  const [h, m] = timeString.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return 0;
  return h * 60 + m;
}

function calculateTotalShiftMinutes(shift) {
  if (!shift?.startTime || !shift?.endTime) return 0;
  const startMinutes = timeToMinutes(String(shift.startTime));
  const endMinutes = timeToMinutes(String(shift.endTime));
  let totalMinutes = endMinutes - startMinutes;
  if (totalMinutes < 0) totalMinutes += 24 * 60;
  return totalMinutes;
}

function buildEmployeeEntriesFromShift(shift) {
  const entries = [];
  const addedEmployeeIds = new Set();

  const addEntry = (employeeId, employeeData = {}) => {
    const normalizedEmployeeId = employeeId || null;
    const isObemannad = Boolean(employeeData?.isObemannad);

    if (!normalizedEmployeeId || isObemannad) return;
    if (addedEmployeeIds.has(normalizedEmployeeId)) return;

    addedEmployeeIds.add(normalizedEmployeeId);

    entries.push({
      employeeId: normalizedEmployeeId,
      employeeData: {
        breaks: employeeData?.breaks || [],
        overtime: employeeData?.overtime || [],
        absences: employeeData?.absences || [],
        ...employeeData,
      },
    });
  };

  if (Array.isArray(shift?.employees) && shift.employees.length > 0) {
    shift.employees.forEach((employeeData) => {
      addEntry(employeeData?.employeeId, employeeData);
    });
  } else {
    addEntry(shift?.employeeId, {
      breaks: shift?.breaks || [],
      overtime: shift?.overtime || [],
      absences: shift?.absences || [],
      employeeId: shift?.employeeId || null,
      isObemannad: shift?.isObemannad || false,
      name: shift?.name || shift?.employee || shift?.userName || "",
    });
  }

  return entries;
}

function StatCard({ icon, value, label, bg, iconColor, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        ...cardStyle,
        padding: "18px 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        minHeight: "96px",
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.2s, box-shadow 0.2s",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
        <div
          style={{
            width: 48,
            height: 48,
            flexShrink: 0,
            borderRadius: "50%",
            background: bg,
            display: "grid",
            placeItems: "center",
            color: iconColor,
          }}
        >
          {icon}
        </div>

        <div>
          <div style={{ fontSize: "13px", color: palette.muted }}>
            {label}
          </div>
          <div
            style={{
              fontSize: "25px",
              fontWeight: 800,
              color: "#272d3b",
              marginBottom: 4,
            }}
          >
            {value}
          </div>
        </div>
      </div>

      <MoreHorizontal size={18} color="#c7cbda" />
    </div>
  );
}

function EmployeesWorkingToday({ employees, shifts, projects }) {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const employeesWorkingToday = useMemo(() => {
    const todayYear = currentTime.getFullYear();
    const todayMonth = currentTime.getMonth();
    const todayDate = currentTime.getDate();
    const nowMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();
    const scheduledList = [];
    const addedRows = new Set();
    const workingEmployeeIds = new Set();

    shifts.forEach((shift) => {
      const shiftDate = safeParseDate(shift.date);
      const isToday =
        shiftDate &&
        shiftDate.getFullYear() === todayYear &&
        shiftDate.getMonth() === todayMonth &&
        shiftDate.getDate() === todayDate;

      if (!isToday || !shift.startTime || !shift.endTime) return;

      const startMinutes = timeToMinutes(String(shift.startTime));
      const endMinutes = timeToMinutes(String(shift.endTime));
      const crossesMidnight = startMinutes > endMinutes;
      const isWorkingNow = crossesMidnight
        ? nowMinutes >= startMinutes || nowMinutes < endMinutes
        : nowMinutes >= startMinutes && nowMinutes < endMinutes;
      const isFinished =
        !isWorkingNow && !crossesMidnight && nowMinutes >= endMinutes;
      const project =
        projects.find(
          (item) =>
            item.id === shift.projectId ||
            item.id === shift.project ||
            item.name === shift.projectName
        ) || null;

      buildEmployeeEntriesFromShift(shift).forEach(
        ({ employeeId, employeeData }) => {
          if (isWorkingNow) {
            workingEmployeeIds.add(employeeId);
            return;
          }

          const rowId = `${shift.id}-${employeeId}`;
          if (addedRows.has(rowId)) return;
          addedRows.add(rowId);

          const employee = employees.find((item) => item.id === employeeId);
          scheduledList.push({
            id: rowId,
            employeeId,
            name:
              employeeData?.name ||
              employee?.name ||
              shift.name ||
              shift.employee ||
              shift.userName ||
              "Okänd personal",
            projectName:
              shift.projectName ||
              project?.name ||
              shift.project ||
              shift.customerName ||
              "Projekt saknas",
            startTime: shift.startTime,
            endTime: shift.endTime,
            status: isWorkingNow
              ? "Pågår nu"
              : isFinished
              ? "Avslutat"
              : "Börjar senare",
            statusColor: isWorkingNow
              ? "#166534"
              : isFinished
              ? "#6b7280"
              : palette.purple,
            statusBackground: isWorkingNow
              ? "#dcfce7"
              : isFinished
              ? "#f3f4f6"
              : "#f3e8ff",
          });
        }
      );
    });

    return scheduledList
      .filter((person) => !workingEmployeeIds.has(person.employeeId))
      .sort((a, b) => String(a.startTime).localeCompare(String(b.startTime)));
  }, [shifts, employees, projects, currentTime]);

  return (
    <div style={{ ...cardStyle, padding: 24, minWidth: 0 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 16,
          marginBottom: 18,
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>
            Personal som jobbar idag
          </h3>
          <p style={{ margin: "5px 0 0", color: palette.muted, fontSize: 13 }}>
            Schemalagda idag som inte är i arbete just nu
          </p>
        </div>
        <div
          style={{
            background:
              employeesWorkingToday.length > 0 ? "#f3e8ff" : "#f3f4f6",
            color:
              employeesWorkingToday.length > 0 ? palette.purple : "#6b7280",
            padding: "8px 12px",
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 700,
            whiteSpace: "nowrap",
          }}
        >
          {employeesWorkingToday.length} antal
        </div>
      </div>

      {employeesWorkingToday.length === 0 ? (
        <div
          style={{
            padding: 18,
            borderRadius: 14,
            background: "#f8f9fc",
            color: palette.muted,
            fontSize: 14,
          }}
        >
          Ingen ytterligare personal att visa för idag.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {employeesWorkingToday.map((person) => (
            <div
              key={person.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 16,
                padding: "14px 16px",
                borderRadius: 12,
                border: `1px solid ${palette.border}`,
                background: "#f8f9fc",
                flexWrap: "wrap",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>
                  {person.name}
                </div>
                <div
                  style={{
                    color: palette.muted,
                    fontSize: 13,
                    marginTop: 4,
                  }}
                >
                  {person.projectName}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ color: palette.muted, fontSize: 13 }}>
                  {person.startTime} - {person.endTime}
                </div>
                <span
                  style={{
                    display: "inline-block",
                    marginTop: 6,
                    padding: "4px 8px",
                    borderRadius: 999,
                    background: person.statusBackground,
                    color: person.statusColor,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {person.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// SEPARAT KOMPONENT FÖR "PERSONAL I ARBETE" - UPPDATERAS VARJE SEKUND
function EmployeesWorkingNow({ employees, shifts, projects }) {
  const [currentTime, setCurrentTime] = useState(new Date());

  // Timer uppdaterar BARA denna komponent varje sekund
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Beräkna personal i arbete - körs varje sekund
  const employeesWorkingNow = useMemo(() => {
    const now = currentTime;
    const todayYear = now.getFullYear();
    const todayMonth = now.getMonth();
    const todayDate = now.getDate();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();

    const isSameDay = (date) =>
      date &&
      date.getFullYear() === todayYear &&
      date.getMonth() === todayMonth &&
      date.getDate() === todayDate;

    const isTimeWithinShift = (shift) => {
      const start = timeToMinutes(String(shift.startTime));
      const end = timeToMinutes(String(shift.endTime));

      if (start <= end) {
        return nowMinutes >= start && nowMinutes < end;
      } else {
        return nowMinutes >= start || nowMinutes < end;
      }
    };

    const workingList = [];
    const addedRows = new Set();

    shifts.forEach((shift) => {
      const shiftDate = safeParseDate(shift.date);
      if (!isSameDay(shiftDate)) return;
      if (!shift.startTime || !shift.endTime) return;
      
      if (!isTimeWithinShift(shift)) return;

      const employeeEntries = buildEmployeeEntriesFromShift(shift);

      employeeEntries.forEach(({ employeeId, employeeData }) => {
        const rowId = `${shift.id}-${employeeId}`;
        if (addedRows.has(rowId)) return;
        addedRows.add(rowId);

        const employee = employees.find((item) => item.id === employeeId);

        const project =
          projects.find(
            (item) =>
              item.id === shift.projectId ||
              item.id === shift.project ||
              item.name === shift.projectName
          ) || null;

        const endMinutes = timeToMinutes(String(shift.endTime));
        const startMinutes = timeToMinutes(String(shift.startTime));
        let remainingMinutes = endMinutes - nowMinutes;
        
        if (remainingMinutes < 0 && startMinutes > endMinutes) {
          remainingMinutes = (24 * 60 - nowMinutes) + endMinutes;
        }

        const remainingHours = Math.floor(remainingMinutes / 60);
        const remainingMins = Math.round(remainingMinutes % 60);

        let remainingText = "";
        if (remainingMinutes > 0) {
          if (remainingHours > 0) {
            remainingText = `${remainingHours}h ${remainingMins}m kvar`;
          } else {
            remainingText = `${remainingMins}m kvar`;
          }
        } else {
          remainingText = "Avslutar nu";
        }

        workingList.push({
          id: rowId,
          name:
            employeeData?.name ||
            employee?.name ||
            shift.name ||
            shift.employee ||
            shift.userName ||
            "Okänd personal",
          projectName:
            shift.projectName ||
            project?.name ||
            shift.project ||
            shift.customerName ||
            "Projekt saknas",
          startTime: shift.startTime,
          endTime: shift.endTime,
          remainingText: remainingText,
          remainingMinutes: remainingMinutes,
        });
      });
    });

    return workingList.sort((a, b) =>
      String(a.startTime).localeCompare(String(b.startTime))
    );
  }, [shifts, employees, projects, currentTime]);

  return (
    <div style={{ ...cardStyle, padding: 24, minWidth: 0 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 16,
          marginBottom: 18,
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>
            Personal i arbete just nu
          </h3>
          <p
            style={{
              margin: "5px 0 0 0",
              color: palette.muted,
              fontSize: 13,
            }}
          >
            Visar personal vars pass pågår just nu
          </p>
        </div>

        <div
          style={{
            background: employeesWorkingNow.length > 0 ? "#dcfce7" : "#f3f4f6",
            color: employeesWorkingNow.length > 0 ? "#166534" : "#6b7280",
            padding: "8px 12px",
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 700,
            whiteSpace: "nowrap",
            transition: "all 0.3s ease",
          }}
        >
          {employeesWorkingNow.length} antal
        </div>
      </div>

      {employeesWorkingNow.length === 0 ? (
        <div
          style={{
            padding: 18,
            borderRadius: 14,
            background: "#f8f9fc",
            color: palette.muted,
            fontSize: 14,
            transition: "all 0.3s ease",
          }}
        >
          Ingen personal är schemalagd att arbeta just nu.
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
            gap: 16,
          }}
        >
          {employeesWorkingNow.map((person) => {
            // FÄRGKODNING:
            // 🟢 GRÖN: Mer än 1 timme (60+ minuter)
            // 🟡 GUL: 10-60 minuter
            // 🔴 RÖD: Mindre än 10 minuter
            
            let borderColor = palette.border;
            let remainingColor = palette.muted;
            let bgColor = "#f8f9fc";
            let pulseSpeed = "1.5s";
            let dotColor = "#22c55e";

            if (person.remainingMinutes < 10 && person.remainingMinutes > 0) {
              // 🔴 RÖD: Mindre än 10 minuter kvar
              borderColor = "#ef4444";
              remainingColor = "#dc2626";
              bgColor = "#fef2f2";
              pulseSpeed = "0.8s";
              dotColor = "#ef4444";
            } else if (person.remainingMinutes < 60) {
              // 🟡 GUL: 10-60 minuter kvar (mindre än 1 timme)
              borderColor = "#eab308";
              remainingColor = "#ca8a04";
              bgColor = "#fefce8";
              pulseSpeed = "1.2s";
              dotColor = "#eab308";
            }
            // 🟢 GRÖN (standard): Mer än 1 timme (60+ minuter) kvar

            return (
              <div
                key={person.id}
                style={{
                  padding: 16,
                  borderRadius: 16,
                  background: bgColor,
                  border: `2px solid ${borderColor}`,
                  transition: "all 0.3s ease",
                  animation: "slideIn 0.3s ease-out",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 10,
                  }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: dotColor,
                      display: "inline-block",
                      animation: `pulse ${pulseSpeed} ease-in-out infinite`,
                    }}
                  />
                  <strong style={{ fontSize: 15 }}>{person.name}</strong>
                </div>

                <div
                  style={{
                    fontSize: 13,
                    color: palette.muted,
                    marginBottom: 6,
                  }}
                >
                  Projekt
                </div>

                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    marginBottom: 10,
                  }}
                >
                  {person.projectName}
                </div>

                <div style={{ fontSize: 13, color: palette.muted }}>
                  {person.startTime} - {person.endTime}
                </div>

                <div
                  style={{
                    marginTop: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    color: remainingColor,
                    background: borderColor + "20",
                    padding: "4px 8px",
                    borderRadius: 6,
                    display: "inline-block",
                  }}
                >
                  {person.remainingText}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { activeCompany, loading: companyLoading, customersAndProjectsEnabled, myCompanyEnabled, reportsEnabled, canAccessPage } = useCompany();
  const showCases = canAccessPage("cases");
  const showReports = reportsEnabled && canAccessPage("reports");
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [showEconomy, setShowEconomy] = useState(false);
  const [economyOpened, setEconomyOpened] = useState(false);

  // State för data - med cache-stöd
  const [employees, setEmployees] = useState([]);
  const [allCustomers, setCustomers] = useState([]);
  const customers = useMemo(() => customersAndProjectsEnabled ? allCustomers : [], [allCustomers, customersAndProjectsEnabled]);
  const [allProjects, setProjects] = useState([]);
  const projects = useVisibleProjects(allProjects);
  const [shifts, setShifts] = useState([]);
  const [overtimeRequests, setOvertimeRequests] = useState([]);
  const [absenceRequests, setAbsenceRequests] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboardLoadError, setDashboardLoadError] = useState(false);
  const [ownerReady, setOwnerReady] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);

  // State för shift IDs
  const [existingShiftIds, setExistingShiftIds] = useState(new Set());

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  // Ref för att lagra företagsägarens info
  const ownerInfoRef = useRef(null);

  const months = useMemo(
    () => [
      { value: 1, label: "Januari" },
      { value: 2, label: "Februari" },
      { value: 3, label: "Mars" },
      { value: 4, label: "April" },
      { value: 5, label: "Maj" },
      { value: 6, label: "Juni" },
      { value: 7, label: "Juli" },
      { value: 8, label: "Augusti" },
      { value: 9, label: "September" },
      { value: 10, label: "Oktober" },
      { value: 11, label: "November" },
      { value: 12, label: "December" },
    ],
    []
  );

  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];

    for (let i = 2020; i <= currentYear + 2; i++) {
      years.push(i);
    }

    return years;
  }, []);

  // HÄMTA ALLA EXISTERANDE SHIFT-ID
  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;

    if (!accountToUse) {
      setExistingShiftIds(new Set());
      return;
    }

    if (globalCache.shiftIdsLoaded && 
        globalCache.shiftIdsCompanyUid === accountToUse) {
      console.log("✅ Dashboard: Använder cached shift IDs");
      setExistingShiftIds(globalCache.shiftIds || new Set());
      return;
    }

    console.log("🔄 Dashboard: Hämtar shift IDs...");

    const shiftsRef = collection(db, "users", accountToUse, "shifts");
    
    const unsubscribe = onSnapshot(shiftsRef, (snapshot) => {
      const shiftIds = new Set();
      snapshot.forEach((doc) => {
        shiftIds.add(doc.id);
      });
      
      globalCache.shiftIds = shiftIds;
      globalCache.shiftIdsCompanyUid = accountToUse;
      globalCache.shiftIdsLoaded = true;
      globalCache.shiftIdsUnsubscribe = unsubscribe;
      
      setExistingShiftIds(shiftIds);
    }, (error) => {
      console.error("Fel vid hämtning av shift-ID:", error);
      setDashboardLoadError(true);
    });

    globalCache.shiftIdsUnsubscribe = unsubscribe;

    return () => {};
  }, [activeCompany?.companyAccountUid]);

  // Hämta företagsinfo och super-admin
  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;

    if (!accountToUse) {
      setCompanyInfo(null);
      ownerInfoRef.current = null;
      globalCache.ownerInfo = null;
      return;
    }

    const userRef = doc(db, "users", accountToUse);

    const unsubscribe = onSnapshot(
      userRef,
      (userSnap) => {
        if (userSnap.exists()) {
          const data = userSnap.data();

          let createdAtDate = null;
          if (data.createdAt) {
            createdAtDate = data.createdAt?.toDate
              ? data.createdAt.toDate()
              : new Date(data.createdAt);
          }

          const info = {
            id: userSnap.id,
            companyId: data.companyId || "",
            companyName: data.companyName || "Företag",
            email: data.email || currentUser?.email || "E-post saknas",
            role: data.role || "admin",
            createdAt: createdAtDate,
          };
          
          setCompanyInfo(info);
          
          // Spara ägarinfo i ref och cache
          if (info.role === "super-admin") {
            const owner = {
              id: info.id,
              name: info.companyName || "Företagsägare",
              firstName: info.companyName || "Företagsägare",
              lastName: "",
              email: info.email || "E-post saknas",
              personalNumber: "–",
              role: "super-admin",
              membershipStatus: "active",
              isOwner: true,
            };
            ownerInfoRef.current = owner;
            globalCache.ownerInfo = owner;
            globalCache.ownerCompanyUid = accountToUse;
          }
        }
        setOwnerReady(true);
      },
      (error) => {
        console.error("Fel vid hämtning av företagsinfo:", error);
        setDashboardLoadError(true);
      }
    );

    return () => unsubscribe();
  }, [activeCompany?.companyAccountUid, currentUser?.email]);

  // Hämta employees med cache - inkludera super-admin
  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;

    if (!accountToUse) {
      setEmployees([]);
      setLoading(false);
      return;
    }

    if (globalCache.employeesLoaded && 
        globalCache.employeesCompanyUid === accountToUse) {
      console.log("✅ Dashboard: Använder cached employees");
      let employeeData = globalCache.employees || [];
      
      // Lägg till super-admin om den inte finns i listan
      const owner = globalCache.ownerInfo || ownerInfoRef.current;
      if (owner) {
        const ownerExists = employeeData.some(user => user.isOwner === true);
        if (!ownerExists) {
          employeeData = [...employeeData, owner];
          // Sortera om så super-admin kommer först
          employeeData.sort((a, b) => {
            if (a.isOwner && !b.isOwner) return -1;
            if (!a.isOwner && b.isOwner) return 1;
            return (a.name || "").localeCompare(b.name || "");
          });
          globalCache.employees = employeeData;
        }
      }
      
      setEmployees(employeeData);
      setLoading(false);
      return;
    }

    console.log("🔄 Dashboard: Hämtar employees...");

    const employedRef = collection(
      db,
      "users",
      accountToUse,
      "employed"
    );

    const unsubscribe = onSnapshot(
      employedRef,
      (snapshot) => {
        const users = [];

        snapshot.forEach((userDoc) => {
          const userData = userDoc.data();

          let fullName = userData.name || "";

          if (userData.firstName && userData.lastName) {
            fullName = `${userData.firstName} ${userData.lastName}`;
          } else if (userData.firstName) {
            fullName = userData.firstName;
          } else if (userData.lastName) {
            fullName = userData.lastName;
          } else if (userData.displayName) {
            fullName = userData.displayName;
          }

          users.push({
            id: userDoc.id,
            ...userData,
            name: fullName || "Användare",
            firstName: userData.firstName || "",
            lastName: userData.lastName || "",
            email: userData.email || "E-post saknas",
            personalNumber: userData.personalNumber || "–",
            role: userData.role || "user",
            membershipStatus:
              userData.membershipStatus || userData.status || "active",
          });
        });

        // Lägg till super-admin (företagsägaren)
        const owner = globalCache.ownerInfo || ownerInfoRef.current;
        if (owner) {
          const ownerExists = users.some(user => user.isOwner === true);
          if (!ownerExists) {
            users.push(owner);
          }
        }

        // Sortera: super-admin först
        users.sort((a, b) => {
          if (a.isOwner && !b.isOwner) return -1;
          if (!a.isOwner && b.isOwner) return 1;
          return (a.name || "").localeCompare(b.name || "");
        });

        globalCache.employees = users;
        globalCache.employeesCompanyUid = accountToUse;
        globalCache.employeesLoaded = true;
        globalCache.employeesUnsubscribe = unsubscribe;

        setEmployees(users);
        setLoading(false);
      },
      (error) => {
        console.error("Fel vid hämtning av anställda:", error);
        setDashboardLoadError(true);
        setLoading(false);
      }
    );

    globalCache.employeesUnsubscribe = unsubscribe;

    return () => {};
  }, [activeCompany?.companyAccountUid]);

  // Hämta customers med cache
  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;

    if (!accountToUse) {
      setCustomers([]);
      return;
    }

    if (globalCache.customersLoaded && 
        globalCache.customersCompanyUid === accountToUse) {
      console.log("✅ Dashboard: Använder cached customers");
      setCustomers(globalCache.customers || []);
      return;
    }

    console.log("🔄 Dashboard: Hämtar customers...");

    const customersRef = collection(
      db,
      "users",
      accountToUse,
      "customers"
    );

    const unsubscribe = onSnapshot(customersRef, (snapshot) => {
      const customerList = [];

      snapshot.forEach((customerDoc) => {
        const data = customerDoc.data();

        customerList.push({
          id: customerDoc.id,
          ...data,
          name: data.name || "Kund saknas",
        });
      });

      globalCache.customers = customerList;
      globalCache.customersCompanyUid = accountToUse;
      globalCache.customersLoaded = true;
      globalCache.customersUnsubscribe = unsubscribe;

      setCustomers(customerList);
    }, (error) => {
      console.error("Fel vid hämtning av kunder:", error);
      setDashboardLoadError(true);
    });

    globalCache.customersUnsubscribe = unsubscribe;

    return () => {};
  }, [activeCompany?.companyAccountUid]);

  // Hämta projects med cache
  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;

    if (!accountToUse) {
      setProjects([]);
      return;
    }

    if (globalCache.projectsLoaded && 
        globalCache.projectsCompanyUid === accountToUse) {
      console.log("✅ Dashboard: Använder cached projects");
      setProjects(globalCache.projects || []);
      return;
    }

    console.log("🔄 Dashboard: Hämtar projects...");

    const projectsRef = collection(
      db,
      "users",
      accountToUse,
      "projects"
    );

    const unsubscribe = onSnapshot(projectsRef, (snapshot) => {
      const projectList = [];

      snapshot.forEach((projectDoc) => {
        const data = projectDoc.data();

        projectList.push({
          id: projectDoc.id,
          ...data,
          name: data.name || "Projekt saknas",
        });
      });

      globalCache.projects = projectList;
      globalCache.projectsCompanyUid = accountToUse;
      globalCache.projectsLoaded = true;
      globalCache.projectsUnsubscribe = unsubscribe;

      setProjects(projectList);
    }, (error) => {
      console.error("Fel vid hämtning av projekt:", error);
      setDashboardLoadError(true);
    });

    globalCache.projectsUnsubscribe = unsubscribe;

    return () => {};
  }, [activeCompany?.companyAccountUid]);

  // Hämta shifts med cache
  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;

    if (!accountToUse) {
      setShifts([]);
      return;
    }

    if (globalCache.shiftsLoaded && 
        globalCache.shiftsCompanyUid === accountToUse) {
      console.log("✅ Dashboard: Använder cached shifts");
      setShifts(globalCache.shifts || []);
      return;
    }

    console.log("🔄 Dashboard: Hämtar shifts...");

    const shiftsRef = collection(
      db,
      "users",
      accountToUse,
      "shifts"
    );

    const unsubscribe = onSnapshot(shiftsRef, (snapshot) => {
      const shiftsList = [];

      snapshot.forEach((shiftDoc) => {
        shiftsList.push({
          id: shiftDoc.id,
          ...shiftDoc.data(),
        });
      });

      globalCache.shifts = shiftsList;
      globalCache.shiftsCompanyUid = accountToUse;
      globalCache.shiftsLoaded = true;
      globalCache.shiftsUnsubscribe = unsubscribe;

      setShifts(shiftsList);
    }, (error) => {
      console.error("Fel vid hämtning av pass:", error);
      setDashboardLoadError(true);
    });

    globalCache.shiftsUnsubscribe = unsubscribe;

    return () => {};
  }, [activeCompany?.companyAccountUid]);

  // Hämta overtime requests med cache och filtrera bort borttagna pass
  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;

    if (!accountToUse) {
      setOvertimeRequests([]);
      return;
    }

    if (globalCache.overtimeLoaded && 
        globalCache.overtimeCompanyUid === accountToUse) {
      console.log("✅ Dashboard: Använder cached overtime requests");
      // Filtrera bort ansökningar där passet tagits bort
      const filtered = (globalCache.overtimeRequests || []).filter((request) => {
        if (!request.shiftId) return true;
        return existingShiftIds.has(request.shiftId);
      });
      setOvertimeRequests(filtered);
      return;
    }

    console.log("🔄 Dashboard: Hämtar overtime requests...");

    const overtimeRef = collection(
      db,
      "users",
      accountToUse,
      "overtimeRequests"
    );

    const unsubscribe = onSnapshot(overtimeRef, (snapshot) => {
      const requests = [];

      snapshot.forEach((doc) => {
        const data = doc.data();

        requests.push({
          id: doc.id,
          ...data,
          date: safeParseDate(data.date),
          submittedAt: safeParseDate(data.submittedAt),
        });
      });

      globalCache.overtimeRequests = requests;
      globalCache.overtimeCompanyUid = accountToUse;
      globalCache.overtimeLoaded = true;
      globalCache.overtimeUnsubscribe = unsubscribe;

      // Filtrera bort ansökningar där passet tagits bort
      const filtered = requests.filter((request) => {
        if (!request.shiftId) return true;
        return existingShiftIds.has(request.shiftId);
      });

      setOvertimeRequests(filtered);
    }, (error) => {
      console.error("Fel vid hämtning av övertid:", error);
      setDashboardLoadError(true);
    });

    globalCache.overtimeUnsubscribe = unsubscribe;

    return () => {};
  }, [activeCompany?.companyAccountUid, existingShiftIds]);

  // Hämta absence requests med cache och filtrera bort borttagna pass
  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;

    if (!accountToUse) {
      setAbsenceRequests([]);
      return;
    }

    if (globalCache.absenceLoaded && 
        globalCache.absenceCompanyUid === accountToUse) {
      console.log("✅ Dashboard: Använder cached absence requests");
      // Filtrera bort ansökningar där passet tagits bort
      const filtered = (globalCache.absenceRequests || []).filter((request) => {
        if (!request.shiftId) return true;
        return existingShiftIds.has(request.shiftId);
      });
      setAbsenceRequests(filtered);
      return;
    }

    console.log("🔄 Dashboard: Hämtar absence requests...");

    const absenceRef = collection(
      db,
      "users",
      accountToUse,
      "absenceRequests"
    );

    const unsubscribe = onSnapshot(absenceRef, (snapshot) => {
      const requests = [];

      snapshot.forEach((doc) => {
        const data = doc.data();

        requests.push({
          id: doc.id,
          ...data,
          date: safeParseDate(data.date),
          startDate: safeParseDate(data.startDate),
          endDate: safeParseDate(data.endDate),
          submittedAt: safeParseDate(data.submittedAt),
        });
      });

      globalCache.absenceRequests = requests;
      globalCache.absenceCompanyUid = accountToUse;
      globalCache.absenceLoaded = true;
      globalCache.absenceUnsubscribe = unsubscribe;

      // Filtrera bort ansökningar där passet tagits bort
      const filtered = requests.filter((request) => {
        if (!request.shiftId) return true;
        return existingShiftIds.has(request.shiftId);
      });

      setAbsenceRequests(filtered);
    }, (error) => {
      console.error("Fel vid hämtning av frånvaro:", error);
      setDashboardLoadError(true);
    });

    globalCache.absenceUnsubscribe = unsubscribe;

    return () => {};
  }, [activeCompany?.companyAccountUid, existingShiftIds]);

  // Hämta reports med cache
  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;

    if (!accountToUse) {
      setReports([]);
      return;
    }

    if (globalCache.reportsLoaded && 
        globalCache.reportsCompanyUid === accountToUse) {
      console.log("✅ Dashboard: Använder cached reports");
      setReports(globalCache.reports || []);
      return;
    }

    console.log("🔄 Dashboard: Hämtar reports...");

    const reportsRef = collection(
      db,
      "users",
      accountToUse,
      "reports"
    );

    const unsubscribe = onSnapshot(reportsRef, (snapshot) => {
      const reportsList = [];

      snapshot.forEach((doc) => {
        const data = doc.data();

        const isUnread =
          data.read === false ||
          data.isRead === false ||
          data.status === "unread" ||
          data.status === "pending";

        reportsList.push({
          id: doc.id,
          ...data,
          createdAt: safeParseDate(data.createdAt),
          date: safeParseDate(data.date),
          isUnread,
        });
      });

      globalCache.reports = reportsList;
      globalCache.reportsCompanyUid = accountToUse;
      globalCache.reportsLoaded = true;
      globalCache.reportsUnsubscribe = unsubscribe;

      setReports(reportsList);
    }, (error) => {
      console.error("Fel vid hämtning av rapporter:", error);
      setDashboardLoadError(true);
    });

    globalCache.reportsUnsubscribe = unsubscribe;

    return () => {};
  }, [activeCompany?.companyAccountUid]);

  const unreadReports = useMemo(() => {
    return reports.filter((report) => report.isUnread === true);
  }, [reports]);

  // Filtrera övertidsansökningar - endast pending och där passet finns
  const filteredOvertimeRequests = useMemo(() => {
    return overtimeRequests.filter((request) => {
      if (request.status !== "pending") return false;

      const requestDate = safeParseDate(request.date);
      if (!requestDate) return false;

      return (
        requestDate.getFullYear() === selectedYear &&
        requestDate.getMonth() + 1 === selectedMonth
      );
    });
  }, [overtimeRequests, selectedYear, selectedMonth]);

  // Filtrera frånvaroansökningar - endast pending och där passet finns
  const filteredAbsenceRequests = useMemo(() => {
    return absenceRequests.filter((request) => {
      if (request.status !== "pending") return false;

      const requestDate = safeParseDate(request.date);
      if (!requestDate) return false;

      return (
        requestDate.getFullYear() === selectedYear &&
        requestDate.getMonth() + 1 === selectedMonth
      );
    });
  }, [absenceRequests, selectedYear, selectedMonth]);

  const statistics = useMemo(() => {
    let unstaffedShiftsCount = 0;

    shifts.forEach((shift) => {
      if (shift.employees && Array.isArray(shift.employees)) {
        shift.employees.forEach((employee) => {
          if (employee?.isObemannad) {
            unstaffedShiftsCount++;
          }
        });
      } else if (shift.isObemannad) {
        unstaffedShiftsCount++;
      }
    });

    // Räkna totalt antal anställda inklusive super-admin
    const totalEmployees = employees.length;

    return {
      cards: [
        {
          icon: <Users size={20} />,
          value: totalEmployees.toString(),
          label: "Antal personal",
          bg: "#eef9ff",
          iconColor: "#4aa8ff",
          onClick: () => navigate("/employees"),
        },
        {
          icon: <UserPlus size={20} />,
          value: customers.length.toString(),
          label: "Antal kunder",
          visible: customersAndProjectsEnabled,
          bg: "#f3eafe",
          iconColor: palette.purple,
          onClick: () => navigate("/customers"),
        },
        {
          icon: <Building2 size={20} />,
          value: projects.length.toString(),
          label: "Antal projekt",
          visible: customersAndProjectsEnabled || myCompanyEnabled,
          bg: "#fff0e8",
          iconColor: palette.orange,
          onClick: () => navigate(customersAndProjectsEnabled ? "/projects" : "/my-company"),
        },
        {
          icon: <FaRegCalendarAlt size={20} />,
          value: unstaffedShiftsCount.toString(),
          label: "Obemannade pass",
          bg: palette.yellowLight,
          iconColor: palette.yellow,
          onClick: () => navigate("/shifts?panel=unstaffed"),
        },
      ],
      pendingReports: unreadReports.length,
      pendingOvertime: filteredOvertimeRequests.length,
      pendingAbsence: filteredAbsenceRequests.length,
    };
  }, [
    shifts,
    employees,
    customers,
    projects,
    unreadReports,
    filteredOvertimeRequests,
    filteredAbsenceRequests,
    customersAndProjectsEnabled,
    myCompanyEnabled,
    navigate,
  ]);

  // Rensa cache när användaren loggar ut
  useEffect(() => {
    if (!activeCompany) {
      if (globalCache.shiftsUnsubscribe) {
        globalCache.shiftsUnsubscribe();
        globalCache.shiftsUnsubscribe = null;
      }
      if (globalCache.employeesUnsubscribe) {
        globalCache.employeesUnsubscribe();
        globalCache.employeesUnsubscribe = null;
      }
      if (globalCache.projectsUnsubscribe) {
        globalCache.projectsUnsubscribe();
        globalCache.projectsUnsubscribe = null;
      }
      if (globalCache.customersUnsubscribe) {
        globalCache.customersUnsubscribe();
        globalCache.customersUnsubscribe = null;
      }
      if (globalCache.overtimeUnsubscribe) {
        globalCache.overtimeUnsubscribe();
        globalCache.overtimeUnsubscribe = null;
      }
      if (globalCache.absenceUnsubscribe) {
        globalCache.absenceUnsubscribe();
        globalCache.absenceUnsubscribe = null;
      }
      if (globalCache.reportsUnsubscribe) {
        globalCache.reportsUnsubscribe();
        globalCache.reportsUnsubscribe = null;
      }
      if (globalCache.shiftIdsUnsubscribe) {
        globalCache.shiftIdsUnsubscribe();
        globalCache.shiftIdsUnsubscribe = null;
      }

      globalCache.shifts = null;
      globalCache.shiftsCompanyUid = null;
      globalCache.shiftsLoaded = false;
      globalCache.employees = null;
      globalCache.employeesCompanyUid = null;
      globalCache.employeesLoaded = false;
      globalCache.projects = null;
      globalCache.projectsCompanyUid = null;
      globalCache.projectsLoaded = false;
      globalCache.customers = null;
      globalCache.customersCompanyUid = null;
      globalCache.customersLoaded = false;
      globalCache.overtimeRequests = null;
      globalCache.overtimeCompanyUid = null;
      globalCache.overtimeLoaded = false;
      globalCache.absenceRequests = null;
      globalCache.absenceCompanyUid = null;
      globalCache.absenceLoaded = false;
      globalCache.reports = null;
      globalCache.reportsCompanyUid = null;
      globalCache.reportsLoaded = false;
      globalCache.ownerInfo = null;
      globalCache.ownerCompanyUid = null;
      globalCache.shiftIds = null;
      globalCache.shiftIdsCompanyUid = null;
      globalCache.shiftIdsLoaded = false;
    }
  }, [activeCompany]);

  const companyUid = activeCompany?.companyAccountUid;
  const dashboardDataReady = companyUid && ownerReady && [
    ["shiftIdsLoaded", "shiftIdsCompanyUid"],
    ["employeesLoaded", "employeesCompanyUid"],
    ["customersLoaded", "customersCompanyUid"],
    ["projectsLoaded", "projectsCompanyUid"],
    ["shiftsLoaded", "shiftsCompanyUid"],
    ["overtimeLoaded", "overtimeCompanyUid"],
    ["absenceLoaded", "absenceCompanyUid"],
    ["reportsLoaded", "reportsCompanyUid"],
  ].every(([loadedKey, uidKey]) => globalCache[loadedKey] && globalCache[uidKey] === companyUid);

  if (companyLoading || (companyUid && !dashboardLoadError && (loading || !dashboardDataReady))) {
    return <LoadingIndicator message="Laddar översikten..." fullPage />;
  }

  if (dashboardLoadError && activeCompany) {
    return <div role="alert" style={{ padding: 28 }}>Kunde inte hämta översikten. Försök ladda om sidan.</div>;
  }

  if (!activeCompany) {
    return (
      <div style={shellStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
            flexDirection: "column",
            gap: 16,
          }}
        >
          <Building2 size={48} color={palette.muted} />
          <p style={{ color: palette.muted }}>
            Inget företag valt. Vänligen logga in igen.
          </p>

          <button
            onClick={() => navigate("/login")}
            style={{
              padding: "10px 20px",
              background: palette.purple,
              color: "#fff",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            Gå till inloggning
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={shellStyle}>
      <style>{`
        @keyframes pulse {
          0% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.4;
            transform: scale(0.8);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .dashboard-content {
          width: min(100%, 1440px);
          margin: 0 auto;
          padding: 0 24px 32px;
          box-sizing: border-box;
        }
        .dashboard-stats {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 16px;
          margin: 20px 0 24px;
        }
        .dashboard-sections {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 20px;
          align-items: start;
        }
        .dashboard-sections > * { min-width: 0; }
        .dashboard-personnel:first-child { grid-column: 1 / -1; }
        .dashboard-personnel {
          display: grid;
          gap: 20px;
          min-width: 0;
        }
        .dashboard-payroll {
          grid-column: 1 / -1;
          width: 100%;
          min-width: 0;
          justify-self: start;
        }
        .dashboard-requests {
          display: grid;
          grid-template-columns: repeat(var(--request-columns, 3), minmax(0, 1fr));
          gap: 10px;
        }
        @media (max-width: 1100px) {
          .dashboard-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .dashboard-sections { grid-template-columns: minmax(0, 1fr); }
        }
        @media (max-width: 600px) {
          .dashboard-content { padding: 0 16px 24px; }
          .dashboard-stats { gap: 10px; }
          .dashboard-requests { grid-template-columns: minmax(0, 1fr); }
        }
        @media (max-width: 420px) {
          .dashboard-stats { grid-template-columns: minmax(0, 1fr); }
        }
      `}</style>

      <div style={{ padding: "20px 30px 0 30px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>
              {showEconomy ? "Ekonomi" : "Dashboard"}
            </h1>
            <p style={{ color: palette.muted, margin: "5px 0 0 0" }}>
              {activeCompany.companyName} • {activeCompany.roleLabel}
            </p>
          </div>
          <div role="group" aria-label="Dashboardvyer" style={{ display: "flex", gap: 8, marginLeft: "auto", flexWrap: "wrap" }}>
            {[
              { label: "Översikt", economy: false, icon: <LayoutDashboard size={18} aria-hidden="true" /> },
              { label: "Ekonomi", economy: true, icon: <Wallet size={18} aria-hidden="true" /> },
            ].map(({ label, economy, icon }) => (
              <button
                key={label}
                type="button"
                onClick={() => {
                  if (economy) setEconomyOpened(true);
                  setShowEconomy(economy);
                }}
                aria-controls="dashboard-view"
                aria-pressed={showEconomy === economy}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "11px 18px",
                  border: `1px solid ${showEconomy === economy ? palette.purple : palette.border}`,
                  borderRadius: 12,
                  background: showEconomy === economy ? palette.purple : palette.card,
                  color: showEconomy === economy ? "#fff" : palette.text,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {icon}
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main>
        <div className="dashboard-content" id="dashboard-view">
          {economyOpened && (
            <div className="dashboard-payroll" hidden={!showEconomy}>
              <PayrollTrendChart
                companyUid={activeCompany.companyAccountUid}
                companyName={activeCompany.companyName}
                projects={projects}
                customersAndProjectsEnabled={customersAndProjectsEnabled}
              />
            </div>
          )}
          {!showEconomy && (
            <>
          <div className="dashboard-stats">
            {statistics.cards.filter((item) => item.visible !== false).map((item) => (
              <StatCard key={item.label} {...item} />
            ))}
          </div>

          <div className="dashboard-sections">
            {(showCases || showReports) && <div style={{ ...cardStyle, padding: 24, minWidth: 0 }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: 16,
                  fontWeight: 800,
                  marginBottom: 20,
                }}
              >
                {showCases && showReports ? "Väntande rapporter & förfrågningar" : showCases ? "Väntande förfrågningar" : "Olästa rapporter"}
              </h3>

              <div
                style={{
                  marginBottom: 20,
                  padding: "14px",
                  background: "#f8f9fc",
                  borderRadius: 12,
                  border: `1px solid ${palette.border}`,
                  display: "flex",
                  gap: 16,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ flex: 1, minWidth: "150px" }}>
                  <label
                    style={{
                      fontSize: 12,
                      color: palette.muted,
                      marginBottom: 6,
                      display: "block",
                    }}
                  >
                    År
                  </label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: 8,
                      border: `1px solid ${palette.border}`,
                      background: "#fff",
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    {availableYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ flex: 1, minWidth: "150px" }}>
                  <label
                    style={{
                      fontSize: 12,
                      color: palette.muted,
                      marginBottom: 6,
                      display: "block",
                    }}
                  >
                    Månad
                  </label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: 8,
                      border: `1px solid ${palette.border}`,
                      background: "#fff",
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    {months.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="dashboard-requests" style={{ "--request-columns": (showCases ? 2 : 0) + (showReports ? 1 : 0) }}>
                {showReports && <div
                  onClick={() => navigate("/reports")}
                  style={{
                    minWidth: 0,
                    background: "#e6f7e6",
                    borderRadius: 16,
                    padding: 16,
                    cursor: "pointer",
                  }}
                >
                  <FileText size={24} color="#16a34a" />
                  <div
                    style={{
                      fontSize: 28,
                      fontWeight: 800,
                      color: "#14532d",
                    }}
                  >
                    {statistics.pendingReports}
                  </div>
                  <div style={{ fontSize: 13, color: "#15803d" }}>
                    olästa rapporter
                  </div>
                </div>}

                {showCases && <div
                  onClick={() => navigate("/overtime-requests")}
                  style={{
                    minWidth: 0,
                    background: palette.indigoLight,
                    borderRadius: 16,
                    padding: 16,
                    cursor: "pointer",
                  }}
                >
                  <Clock size={24} color={palette.indigo} />
                  <div
                    style={{
                      fontSize: 28,
                      fontWeight: 800,
                      color: "#1e1b4b",
                    }}
                  >
                    {statistics.pendingOvertime}
                  </div>
                  <div style={{ fontSize: 13, color: "#4338ca" }}>
                    väntande övertidsförfrågningar
                  </div>
                </div>}

                {showCases && <div
                  onClick={() => navigate("/absence-requests")}
                  style={{
                    minWidth: 0,
                    background: palette.redLight,
                    borderRadius: 16,
                    padding: 16,
                    cursor: "pointer",
                  }}
                >
                  <CalendarX size={24} color={palette.red} />
                  <div
                    style={{
                      fontSize: 28,
                      fontWeight: 800,
                      color: "#7f1d1d",
                    }}
                  >
                    {statistics.pendingAbsence}
                  </div>
                  <div style={{ fontSize: 13, color: "#991b1b" }}>
                    väntande frånvaroförfrågningar
                  </div>
                </div>}
              </div>
            </div>}

            <div className="dashboard-personnel">
              <EmployeesWorkingToday
                employees={employees}
                shifts={shifts}
                projects={projects}
              />
              <EmployeesWorkingNow
                employees={employees}
                shifts={shifts}
                projects={projects}
              />
            </div>
          </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
